import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Popover,
  PopoverActions,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ConfirmationDialog from "components/confirmationDialog/ConfirmationDialog";
import { extractAgenda } from "api/meet/meetAPI";
import { formatFileSize } from "utils/formatFileSize";
import {
  ACCEPTED_AGENDA_ATTRIBUTE,
  ACCEPTED_AGENDA_EXTENSIONS,
  AGENDA_POPOVER_WIDTH_PX,
  MAX_AGENDA_SIZE_BYTES,
  MAX_AGENDA_SIZE_MB,
} from "./agendaConstants";

// What the popover hands back when the user asks for a report. The file itself never leaves the
// browser: the extract endpoint turns it into text at attach time and only that text rides the
// summary POST.
export type AgendaGeneratePayload = {
  agendaText: string;
  instructions: string;
  fileName: string;
};

type AgendaAttachPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  meetingName: string;
  // Whether this row is treated as already carrying a stored AI report. It selects which of the
  // two confirm-copy variants below is shown, and NOTHING else - in particular it never reaches
  // the wire and never changes what `onGenerate` is called with.
  //
  // The parent owns the question of how it is decided, including what an unknown row reads as.
  hasExistingReport: boolean;
  onClose: () => void;
  onGenerate: (payload: AgendaGeneratePayload) => void;
};

// Keep the panel this far from the bottom edge of the window when deciding whether it fits
// below the caret.
const VIEWPORT_MARGIN_PX = 16;
// Reserve above the caret before flipping up. The app bar occupies the top of the viewport, and
// a panel flipped into it gets clamped by MUI.
const TOP_RESERVE_PX = 60;

/**
 * The client's share of file validation. Returns the message to show, or "" when the file passes.
 *
 * Tested on the EXTENSION, not the MIME type: browsers report an unreliable type for Office
 * files, so `File.type` is worth nothing here. The extract endpoint's own rejection is the real
 * gate, and it renders in the same Alert as these two messages.
 *
 * The size message keeps CANmail AttachmentModal's exact shape, including the unspaced `10MB`.
 */
const validateAgendaFile = (candidate: File): string => {
  const lowerName = candidate.name.toLowerCase();
  const accepted = ACCEPTED_AGENDA_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
  if (!accepted) {
    return `${candidate.name} is not a supported file type. Choose a .docx, .pdf, or .txt file.`;
  }
  if (candidate.size > MAX_AGENDA_SIZE_BYTES) {
    return `File size (${formatFileSize(candidate.size)}) exceeds ${MAX_AGENDA_SIZE_MB}MB limit`;
  }
  return "";
};

/**
 * The message for a file the extract endpoint READ successfully and found no text in.
 *
 * This is not an error the server reports: a scanned PDF answers `200 { text: "" }`, so the call
 * succeeds and the caller has to notice the emptiness itself. Without this check the file attaches,
 * the chip renders, and the confirmed generate replaces the stored report in DEFAULT mode while the
 * progress dialog's chip names an agenda file.
 *
 * Two sentences, picked on the extension, because the cause differs: a PDF that reads empty is
 * almost always a scan (images with no text layer), while any other empty file is simply empty.
 * Recognising an image-only PDF and running OCR on it is deliberately not attempted here.
 */
const emptyExtractionMessage = (fileName: string): string =>
  fileName.toLowerCase().endsWith(".pdf")
    ? "No readable text was found in this PDF — it appears to be a scanned copy (images only). Attach a text-based PDF, Word, or .txt file instead."
    : "No readable text was found in this file — it appears to be empty. Attach a text-based PDF, Word, or .txt file instead.";

// The confirm that fires on EVERY generate from this panel, replacement or not. Two variants,
// because one sentence cannot be true of both: the shipped copy asserts "This meeting already
// has an AI report", which is false on a row that has none.
//
// The replace variant is the shipped wording, unchanged to the character. The fresh variant makes
// no claim about an existing report and promises nothing about how long the work takes.
const REPLACE_REPORT_CONFIRM = {
  title: "Replace the existing report?",
  description:
    "This meeting already has an AI report. Generating with this agenda will write a new report and replace the current one. The existing report cannot be recovered.",
  confirmText: "Replace Report",
};

const FRESH_REPORT_CONFIRM = {
  title: "Generate an AI report for this meeting?",
  description:
    "CANai will read this meeting's transcript together with the attached agenda and write the report.",
  confirmText: "Generate Report",
};

/**
 * Pull a renderable message off a failed extract call.
 *
 * Same shape as MeetingHistory's toSummaryErrorMessage, and for the same reason: the 403 session
 * interceptor in api/axiosInstance.ts rejects with a BARE Error that has no `.response` at all,
 * so `error.message` is the only thing that survives that case.
 */
const toExtractErrorMessage = (error: any, fallback: string): string => {
  const candidate = error?.response?.data?.error ?? error?.message;
  return typeof candidate === "string" && candidate.trim().length > 0 ? candidate : fallback;
};

/**
 * The agenda attach panel, opened by the caret on a row's AI Report button.
 *
 * State lives in local useState, which is CANmeet's own idiom - this module has no Redux slice
 * and no React Query. The parent remounts this component per row (it keys on the recording id),
 * so opening a different row's caret starts with no file, no error and no instructions.
 */
const AgendaAttachPopover: React.FC<AgendaAttachPopoverProps> = ({
  open,
  anchorEl,
  meetingName,
  hasExistingReport,
  onClose,
  onGenerate,
}) => {
  const [file, setFile] = useState<File | null>(null);
  // The extracted plain text, held in component state because it rides the summary POST later.
  const [agendaText, setAgendaText] = useState<string>("");
  // The name of the file currently being extracted, or "" when nothing is in flight. Doubles as
  // the wait flag and as the label on the wait row.
  const [extractingName, setExtractingName] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const [dragOver, setDragOver] = useState<boolean>(false);
  // One error surface for the whole panel: the two client-side rejections and the extract
  // endpoint's own rejection all render here. "" means no error.
  const [errorMessage, setErrorMessage] = useState<string>("");
  // Every generate from this panel is confirmed, not only a replace. The recordings list carries
  // no report field, so the always-on confirm is what carries the replace warning; `hasExistingReport`
  // only decides which words it uses.
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  // True when the panel would run past the fold below the caret and there is room above it.
  const [flipUp, setFlipUp] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  // MUI's own handle for "recompute where you are". The DataGrid body scrolls inside the page
  // rather than scrolling the window, so Popover never hears about it on its own.
  const popoverActionRef = useRef<PopoverActions | null>(null);

  // MUI already clamps the panel inside the viewport, so this is not what stops it clipping - it
  // is what makes a panel near the bottom of the window sit ABOVE the caret rather than on top of
  // the row that opened it.
  const updatePlacement = () => {
    const content = contentRef.current;
    if (!anchorEl || !content) {
      return;
    }
    const anchorRect = anchorEl.getBoundingClientRect();
    const height = content.offsetHeight;
    const next =
      anchorRect.bottom + height + VIEWPORT_MARGIN_PX > window.innerHeight &&
      anchorRect.top - height > TOP_RESERVE_PX;
    // Only ever assign on a real change: this runs after every render, and an unconditional
    // setState here would re-render forever.
    setFlipUp((current) => (current === next ? current : next));
  };

  // No dependency array on purpose. The panel's height changes with its content - dropzone, then
  // chip, then chip plus an error Alert - and each of those is already a render, so re-measuring
  // on every render is both the simplest and the most accurate trigger.
  useEffect(() => {
    if (!open) {
      return;
    }
    updatePlacement();
  });

  // `true` for capture: scroll does not bubble, so a listener on window only hears about the
  // DataGrid's internal scroller during the capture phase. Resize is handled here as well so the
  // flip decision and MUI's own position are recomputed together.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleReposition = () => {
      updatePlacement();
      popoverActionRef.current?.updatePosition();
    };
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
    // anchorEl is a dependency because the caret can change row while the panel stays open, and
    // the handler above closes over it.
  }, [open, anchorEl]);

  // One file, always. A multi-file drop or a multi-select takes the first and discards the rest,
  // and attaching again replaces whatever was there.
  const attachFile = async (selected: File | null | undefined) => {
    if (!selected) {
      return;
    }
    const validationError = validateAgendaFile(selected);
    if (validationError) {
      // Inline, in the panel, never a toast. Nothing is attached, so the dropzone stays on screen
      // and the generate control stays disabled.
      setFile(null);
      setErrorMessage(validationError);
      return;
    }

    // The extract call is what makes the attach real. A file the server will not read is not
    // attached, so nothing is committed to state until the text comes back.
    setErrorMessage("");
    setExtractingName(selected.name);
    try {
      const response = await extractAgenda(selected);
      const extractedText = String(response?.text ?? "");
      // A 200 carrying no text is a REJECTION, not a success. The endpoint answers
      // `200 { text: "", thinExtraction: true }` for a scanned PDF, and an attach made on that
      // answer sends a summary POST with no `agendaText` - which runs the generation in default
      // mode, replaces the stored report, and names an agenda file on the progress dialog the
      // whole time. Trimmed, so a file whose only content is whitespace is treated the same way.
      //
      // Same containment as the two client-side rejections above: nothing attaches, the dropzone
      // stays on screen, the generate control stays disabled, and picking another file recovers.
      if (extractedText.trim().length === 0) {
        setFile(null);
        setAgendaText("");
        setErrorMessage(emptyExtractionMessage(selected.name));
        return;
      }
      setAgendaText(extractedText);
      setFile(selected);
    } catch (error: any) {
      // Same inline Alert as the two client-side rejections, deliberately.
      setFile(null);
      setAgendaText("");
      setErrorMessage(
        toExtractErrorMessage(error, "The agenda could not be read. Please try another file.")
      );
    } finally {
      setExtractingName("");
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void attachFile(event.target.files?.[0]);
    // Clear the input's own value so re-picking the SAME file still fires a change event.
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragOver(false);
    void attachFile(event.dataTransfer?.files?.[0]);
  };

  const handleClearFile = () => {
    setFile(null);
    setAgendaText("");
  };

  // Asks first, sends nothing. The request is made only by handleConfirmedGenerate below.
  const handleGenerateClick = () => {
    if (!file) {
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmedGenerate = () => {
    if (!file) {
      return;
    }
    setConfirmOpen(false);
    onGenerate({
      agendaText,
      // Trimmed so that whitespace-only input behaves exactly like an empty field: the wire
      // omits the key entirely rather than sending a blank string.
      instructions: instructions.trim(),
      fileName: file.name,
    });
  };

  const extracting = extractingName !== "";

  // Which confirm the panel is about to show. Picked here rather than inline so the dialog below
  // reads as one control with three strings, not three conditionals.
  const confirmCopy = hasExistingReport ? REPLACE_REPORT_CONFIRM : FRESH_REPORT_CONFIRM;

  // Why the generate control is unavailable, or "" when it is available. MUI renders no tooltip
  // for an empty title, so one expression covers every state.
  const generateDisabledReason = extracting
    ? "Reading the agenda file"
    : file
      ? ""
      : "Attach an agenda first";

  return (
    <>
      <Popover
        action={popoverActionRef}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        // Right-aligned explicitly. The width idiom below comes from CANpark's filterPopover, but
        // that popover is LEFT-anchored and cannot be copied for this part - this panel's right
        // edge anchors to the caret's right edge.
        anchorOrigin={{ vertical: flipUp ? "top" : "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: flipUp ? "bottom" : "top", horizontal: "right" }}
      >
        <Box
          ref={contentRef}
          sx={{ p: 2, width: AGENDA_POPOVER_WIDTH_PX, maxWidth: "calc(100vw - 32px)" }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon fontSize="small" color="secondary" />
            <Typography variant="body2" fontWeight={600}>
              Generate with agenda
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
            sx={{ ml: "26px", mt: "1px", mb: 1.5 }}
          >
            {meetingName}
          </Typography>

          {extracting ? (
            // The wait, in the panel, while the extract call is in flight. The file is not
            // attached yet - it becomes attached only when the text comes back - so this row
            // deliberately does not look like the chip below it.
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {`Reading ${extractingName}...`}
              </Typography>
            </Box>
          ) : file ? (
            <Box sx={{ display: "flex", mb: 1.5 }}>
              <Chip
                size="small"
                icon={<AttachFileIcon />}
                label={`${file.name} (${formatFileSize(file.size)})`}
                onDelete={handleClearFile}
                sx={{ maxWidth: "100%" }}
              />
            </Box>
          ) : (
            <Paper
              variant="outlined"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                px: 1.5,
                py: 1.25,
                mb: 1.5,
                textAlign: "center",
                cursor: "pointer",
                borderStyle: "dashed",
                bgcolor: dragOver ? "action.hover" : "background.paper",
                borderColor: dragOver ? "primary.main" : "divider",
                transition: "all 0.2s",
                "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_AGENDA_ATTRIBUTE}
                onChange={handleInputChange}
                style={{ display: "none" }}
              />
              <AttachFileIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              <Typography variant="body2">Drag and drop an agenda here</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block">
                {`.docx, .pdf or .txt — maximum ${MAX_AGENDA_SIZE_MB} MB`}
              </Typography>
            </Paper>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {errorMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            multiline
            // Four fixed rows, not minRows/maxRows. The nearest precedent (AlertRuleDialog's CANai
            // prompt) grows from 1 to 3; four lines at rest is a deliberate override, so the field
            // scrolls internally past them rather than growing the panel.
            rows={4}
            placeholder="Optional instructions for CANai, e.g. 'Flag agenda items that were not discussed'"
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            {/* A disabled MUI Button emits no pointer events, so the Tooltip has to wrap a span
                rather than the button itself. STooltip cannot be reused here - it hardcodes an
                IconButton and takes an `icon` prop. */}
            <Tooltip title={generateDisabledReason}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleGenerateClick}
                  disabled={!file || extracting}
                >
                  Generate Report
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Popover>

      {/* The shared dialog, not a hand-built one: its defaults already give an outlined Cancel and
          a color="error" variant="contained" confirm, which is the anatomy this dialog needs. Note
          this is components/confirmationDialog/ConfirmationDialog, not the unrelated file of the
          same name under CANparkAdmin. */}
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmedGenerate}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmText={confirmCopy.confirmText}
        cancelText="Cancel"
      />
    </>
  );
};

export default AgendaAttachPopover;
